import { useApiStore } from '@/context/global/stores/apiStore';
import { useKaraokeStore } from '@/context/global/stores/karaokeStore';
import { getOrCreateDeviceId } from '@/features/auth/utils/deviceId';
import { useEffect, useRef, useState, useCallback } from 'react';

const HEARTBEAT_INTERVAL_MS = 10000; // 10 seconds
const HEARTBEAT_MAX_DURATION_MS = 60000; // 60 seconds

type UsePlaybackHeartbeatOptions = {
  isPlaying: boolean;
  songId: number | null;
  onHeartbeat?: (accumulatedSeconds: number) => void;
};

export const usePlaybackHeartbeat = ({
  isPlaying,
  songId,
  onHeartbeat,
}: UsePlaybackHeartbeatOptions) => {
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatStartTimeRef = useRef<number | null>(null);

  const { currentSong, sessionId } = useKaraokeStore();
  const { API } = useApiStore();

  const deviceId = getOrCreateDeviceId();

  // Empty function - user will add API call here
  const handleSendHeartbeat = useCallback(() => {
    if (process.env.NODE_ENV === 'development')
      console.log('[Heartbeat] - sendHeartBeat');

    if (!currentSong?.id || !sessionId) return;

    API.Song.PostStatHeartBeat(currentSong.id, deviceId, sessionId);
  }, [API.Song, currentSong?.id, deviceId, sessionId]);

  // Reset accumulated time when song changes
  useEffect(() => {
    setAccumulatedSeconds(0);
  }, [songId]);

  // Heartbeat sender - runs every 10 seconds, stops after 60 seconds
  useEffect(() => {
    if (isPlaying && songId) {
      heartbeatStartTimeRef.current = Date.now();

      heartbeatIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - (heartbeatStartTimeRef.current ?? Date.now());

        if (elapsed >= HEARTBEAT_MAX_DURATION_MS) {
          if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
          }
          return;
        }

        handleSendHeartbeat();
      }, HEARTBEAT_INTERVAL_MS);
    } else {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      heartbeatStartTimeRef.current = null;
    }

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [isPlaying, songId, handleSendHeartbeat]);

  // Start/stop interval based on isPlaying
  useEffect(() => {
    if (isPlaying && songId) {
      intervalRef.current = setInterval(() => {
        setAccumulatedSeconds((prev) => {
          const newValue = prev + 1;
          onHeartbeat?.(newValue);

          if (process.env.NODE_ENV === 'development') {
            console.log(`[Heartbeat] Song ${songId} - ${newValue}s played`);
          }

          return newValue;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, songId, onHeartbeat]);

  return { accumulatedSeconds };
};
